import { useCallback, useEffect, useRef } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import _ from 'lodash';
import { config } from '_config';
import { useStoreValue } from 'store';
import { getFeatureJsonValue, getFeatureValue, findChannelSpecificToggle } from 'featureToggles';
import { useHandleApiErrors } from './useHandleApiErrors';

export const OFFERS_GRAPHQL = gql`
  query offers($productCode: String!, $promoCode: String!) {
    offers(productCode: $productCode, promoCode: $promoCode) {
      status
      offerType
      shortDescription
      promoStart
      promoEnd
    }
  }
`;

function useValidateOffers({ onError }) {
  const [storeState, updateStore] = useStoreValue();
  const { applyAndBuy } = storeState;
  const storeStateRef = useRef(storeState);
  storeStateRef.current = storeState;

  const { handleError } = useHandleApiErrors('hook:useValidateOffers', onError);
  const offerTypeMapping = getFeatureValue('offerTypeMapping');

  const retrievePromoCode = useCallback(applyAndBuyParams => {
    if (applyAndBuyParams?.promoCode) {
      return applyAndBuyParams.promoCode;
    }

    const defaultPromoCodeConfig = getFeatureJsonValue('defaultPromoCode');
    if (_.isString(defaultPromoCodeConfig)) {
      return defaultPromoCodeConfig;
    }

    if (!defaultPromoCodeConfig) {
      return null;
    }

    return findChannelSpecificToggle(applyAndBuyParams, defaultPromoCodeConfig);
  }, []);

  const processOfferData = useCallback(
    data => {
      const { status, promoStart, promoEnd, offerType, shortDescription } = data.offers;
      const currentStoreState = storeStateRef.current;
      const currentApplyAndBuy = currentStoreState.applyAndBuy;

      if (status !== 'VALID') {
        const nextApplyAndBuy = { ...currentApplyAndBuy, promoCode: undefined };
        const offer = {
          status,
        };

        if (_.isEqual(currentStoreState.offer, offer) && _.isEqual(currentApplyAndBuy, nextApplyAndBuy)) {
          return;
        }

        updateStore({
          ...currentStoreState,
          applyAndBuy: nextApplyAndBuy,
          offer,
        });
        return;
      }

      const offer = {
        status,
        type: offerType,
        title: offerTypeMapping[offerType?.toUpperCase()],
        description: shortDescription,
        promoStart: convertUTCToLocalDate(promoStart),
        promoEnd: convertUTCToLocalDate(promoEnd),
      };

      const promoCode = retrievePromoCode(currentApplyAndBuy);
      const nextApplyAndBuy = { ...currentApplyAndBuy, promoCode };

      if (_.isEqual(currentStoreState.offer, offer) && _.isEqual(currentApplyAndBuy, nextApplyAndBuy)) {
        return;
      }

      updateStore({
        ...currentStoreState,
        offer,
        applyAndBuy: nextApplyAndBuy,
      });
    },
    [offerTypeMapping, retrievePromoCode, updateStore],
  );

  const [valid, { loading, data, error }] = useLazyQuery(OFFERS_GRAPHQL);

  useEffect(() => {
    if (data?.offers) {
      processOfferData(data);
    }
  }, [data, processOfferData]);

  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error, handleError]);

  const validateOffer = useCallback(() => {
    const input = {
      productCode: config.productCode,
      promoCode: retrievePromoCode(applyAndBuy),
    };
    valid({ variables: input }).then();
  }, [applyAndBuy, retrievePromoCode, valid]);

  return {
    validateOffer,
    loading,
    retrievePromoCode,
  };
}

function convertUTCToLocalDate(utcTime) {
  const utcDate = new Date(utcTime);
  if (isNaN(utcDate)) {
    return null;
  }
  return utcDate.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export { useValidateOffers };
